<template>
	<div
		@mouseover="onMouseOver"
		@mouseleave="onMouseLeave"
		ref="menu_item_container"
		@click="onMenuItemClick(item, itemKey)"
		class="data-input-item-container flex-space-between--center width-100"
		:class="{ 'is-focused': isFocused, disabled: disabled, 'is-different-item-focoused': focusedMenuItemKey && !isFocused }"
	>
		<FormulaSelectorMenu
			:formulas="formulas"
			allowKeyboardNavigation
			autoFocus
			@show-formula-update="(v) => (showFormula = v)"
			@on-formula-selected="onFormulaSelected"
			:showFormula="isShowFormulaMenu"
		>
			<template #activator>
				<div
					:class="{ 'is-focused': isFocused, disabled: disabled }"
					class="input-item-container display-flex flex-row width-100 flex-align-center"
				>
					<DataTypeIcon class="mr-1 data-type-icon" :itemValue="value" />
					<div class="item-key" v-html="markSearchString(getCleanHtml(getItemKeyTitle(item.key, itemKey, item.workflowId)))" />
					<div class="highlightText item-value-container ml-2">
						<span v-html="markSearchString(getCleanHtml(value))" class="item-value"></span>
					</div>
				</div>
			</template>
		</FormulaSelectorMenu>
		<DataItemInfoCard
			:item="item"
			:itemValue="value"
			:itemKey="itemKey"
			:stepIndex="stepIndex"
			:infoCardTop="infoCardTop"
			:searchString="searchString"
		/>
	</div>
</template>

<script>
import { sanitizeHtml } from '@/utils/helpers';
import { DataTypeEvaluator } from '@ply-io/data-types';

import { markString } from './../../helpers/helpers';
import DataInputMenuItemBaseMixin from './DataInputMenuItemBaseMixin';

import FormulaSelectorMenu from '@/components/DataTableRepresentation/FormulaSelectorMenu';
import DataItemInfoCard from '@/components/DataInput/DataInputMenu/MenuLists/helpers/DataItemInfoCard';

import FormulasService from '@/components/common/RichTextBox/FormulaChip/formulas-service';
import DataInputMenuService from '@/components/DataInput/DataInputMenu/data-input-menu-service';

const evaluator = new DataTypeEvaluator();

export default {
	name: 'DataInputMenuItem',
	mixins: [DataInputMenuItemBaseMixin],
	props: {
		item: { type: Object },
		itemKey: { type: String },
		stepIndex: { type: Number },
		searchString: { type: String },
		isTableOnly: { type: Boolean },
		contentRectYAxis: { type: Number },
		focusedMenuItemKey: { type: String },
		dataInputParam: { type: Object },
		value: { type: [String, Number, Array, Object, Boolean] },
	},
	components: {
		DataItemInfoCard,
		FormulaSelectorMenu,
	},
	computed: {
		isFocused() {
			return this.focusedMenuItemKey === this.menuItemUniqueKey;
		},
		menuItemUniqueKey() {
			return DataInputMenuService.getMenuItemUniqueKey(this.item, this.itemKey);
		},
		disabled() {
			return this.isTableOnly;
		},
		formulas() {
			const { item, itemKey } = this;
			const format = this.dataInputParam?.format;
			const currentField = item.items[itemKey];
			return format && typeof currentField === 'string'
				? FormulasService.getPossibleFormulas(format, evaluator.isMarkdown(String(currentField)), evaluator.isHtml(String(currentField)))
				: [];
		},
	},
	methods: {
		getCleanHtml(dirtyContent) {
			return sanitizeHtml(String(dirtyContent));
		},
		getInforCardTop() {
			const itemTop = this.$refs.menu_item_container?.getBoundingClientRect().y;
			if (this.contentRectYAxis > itemTop) {
				return this.contentRectYAxis;
			}
			return itemTop;
		},
		onMouseOver() {
			if (this.disabled) {
				return;
			}
			this.setInfoCardPosition();
			this.focusedMenuItemKey && this.$emit('focus-on-item', null); // clear focused menu item if hovering on another
		},
		onMouseLeave() {
			setTimeout(() => {
				this.isShowFormulaMenu = false;
			}, 700);
		},
		setInfoCardPosition() {
			this.infoCardTop = this.getInforCardTop();
		},
		onMenuItemClick(item, itemKey) {
			if (this.disabled) {
				return;
			}

			const itemValue = String(item.items[itemKey]); // We are sending only strings to isMarkdown /is Html
			if (this.formulas.length > 0 && (evaluator.isMarkdown(itemValue) || evaluator.isHtml(itemValue))) {
				this.isShowFormulaMenu = true;
			} else {
				this.$emit('set-data-input-menu-item', item, itemKey);
			}
		},
		markSearchString(str) {
			return markString(this.searchString, str);
		},
		onFormulaSelected(formulaName) {
			const { item, itemKey } = this;
			this.isShowFormulaMenu = false;
			this.$emit('set-data-input-menu-item', item, itemKey, formulaName);
		},
		openFormulasMenu() {
			if (this.isFocused) {
				this.onMenuItemClick(this.item, this.itemKey);
			}
		},
	},
	watch: {
		isFocused: {
			handler(newV) {
				if (newV) {
					const menuItemRect = this.$refs.menu_item_container?.getBoundingClientRect() || {};
					this.$emit('scroll-to-element', menuItemRect);
					this.setInfoCardPosition();
				}
			},
		},
	},
	data() {
		return {
			infoCardTop: 0,
			isShowFormulaMenu: false,
		};
	},
};
</script>

<style scoped lang="scss">
.disabled {
	cursor: default;
}
.data-input-item-container {
	&:hover,
	&.is-focused {
		&:not(.is-different-item-focoused) {
			.data-input-item-info-card-container {
				display: flex;
				padding: 6px;
				position: fixed;
				margin-left: -262px;
			}
		}
	}
	&.disabled {
		opacity: 0.5;
		cursor: auto;
		.data-input-item-info-card-container {
			display: none;
		}
	}
	.input-item-container {
		height: 25px;
		cursor: pointer;
		font-size: 12px;
		padding: 0px 6px;
		background: var(--bg-color);

		&.disabled {
			opacity: 0.5;
			cursor: auto;
			.data-input-item-info-card-container {
				display: none;
			}
		}

		&:hover,
		&.is-focused {
			&:not(.disabled) {
				background: var(--ask-for-ingreation-hover-background);
			}
		}
		.data-type-icon {
			margin-top: 4px;
		}
		.item-key {
			min-width: 8%;
			max-width: 85%;
			overflow: hidden;
			padding: 0px 2px;
			border-radius: 3px;
			white-space: nowrap;
			text-overflow: ellipsis;
			color: var(--primary-font-color);
			background: var(--data-chip-background);
		}
		.item-value {
			color: var(--secondary-font-color);
		}

		.item-value-container {
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}
	}
}
</style>
